import { useQuery } from '@tanstack/react-query';
import useApi from '../utils/AxiosWrapper';
import { DispatchPricing } from '../constants/loadRequest';
import { parseDispatchPricing } from '../utils/dispatchPricing';
import { unwrapApiResponse } from '../utils/unwrapApiResponse';

/**
 * GET /emails/load-request/pricing → the dispatch fee tiers for the Find Me
 * Freight flow (THT-691), resolved server-side from equipment tier + the
 * carrier's authority age. Rendered on the review step only.
 *
 * Display-only: a failure NEVER blocks the flow. The review row simply reads
 * `data` (undefined on error) and falls back to the neutral "your dispatcher
 * will confirm" line — `useQuery` doesn't throw to the caller, so the error
 * state costs the user nothing.
 *
 * What it must not do is fail SILENTLY. This used to hand-roll the unwrap and
 * map every failure to `null`, which made a persistent 4xx/5xx on this brand
 * new endpoint indistinguishable from an empty payload and invisible to
 * Sentry — the fee could be missing for every carrier in the funnel and
 * nothing would say so. Going through `unwrapApiResponse` puts a transport or
 * HTTP failure into React Query's error state, where the global QueryCache
 * onError hook reports it once (see appQueryClient.ts / reportApiError.ts).
 * Degrading in the UI and reporting to Sentry are not in tension.
 *
 * The backend wraps every payload in its `HttpResponse` envelope
 * (`{ message, data }`), so the pricing object is one level down —
 * `unwrapApiResponse` is exactly that unwrap. Reading `response.data` instead
 * yields the envelope, whose `standard_pct` is undefined, and the row would
 * silently show "rates unavailable" forever (the bug fixed on mobile in
 * THT-691).
 */
export function useLoadRequestPricingQuery(enabled: boolean) {
  const { callProtectedApi } = useApi();

  return useQuery<DispatchPricing | null>({
    queryKey: ['loadRequest', 'pricing'],
    queryFn: async () => {
      const response = await callProtectedApi(
        '/emails/load-request/pricing',
        'get',
        null,
        // No errorMsg: a pricing failure degrades to the neutral fallback
        // line, never a toast on top of a flow the user can still finish.
        undefined
      );
      const data = unwrapApiResponse<unknown>(
        response,
        'Failed to load dispatch pricing.'
      );
      // A 2xx carrying a payload we can't read is a real (reportable) defect
      // too, but it isn't an API failure — keep it out of the error path and
      // let the guard degrade it to the same neutral line.
      return parseDispatchPricing(data);
    },
    enabled,
    // Rates change on the order of pricing decisions, not minutes, so reopening
    // the modal reuses the cached value instead of refetching every time.
    staleTime: 5 * 60 * 1000,
    // Display-only: a retry storm buys nothing the fallback line doesn't.
    retry: false,
  });
}
